// src/app/mock/mock-response.util.ts
//
// Shared response builder utilities for all mock handlers.
// Every builder accepts an optional `delayMs` parameter.
//
// READ  operations (GET list / GET by id) use no delay by default — they should
// feel instant so developers aren't blocked while browsing.
//
// WRITE operations (POST / PUT / DELETE) default to MOCK_WRITE_DELAY_MS so the
// loading spinner, button disabled state, and success/error dialogs are all
// visible during development — exactly as they would be on a real network.

import { HttpResponse } from '@angular/common/http';
import { delay } from 'rxjs/operators';
import { of } from 'rxjs';

/** Default delay applied to create / update / delete responses (ms). */
export const MOCK_WRITE_DELAY_MS = 1100;

/** No delay for read operations. */
export const MOCK_READ_DELAY_MS = 1100;

// ── Builders ─────────────────────────────────────────────────────────────────

export function okList<T>(data: T[], page = 0, size = 10, delayMs = MOCK_READ_DELAY_MS) {
    return of(
        new HttpResponse({
            status: 200,
            body: {
                code: 200,
                message: 'success',
                data,
                meta: { page, size, totalRecords: data.length }
            }
        })
    ).pipe(delay(delayMs));
}

export function okSingle<T>(data: T, delayMs = MOCK_READ_DELAY_MS) {
    return of(
        new HttpResponse({
            status: 200,
            body: { code: 200, message: 'success', data }
        })
    ).pipe(delay(delayMs));
}

export function okResults<T>(data: T[], delayMs = MOCK_READ_DELAY_MS) {
    return of(
        new HttpResponse({
            status: 200,
            body: { code: 200, message: 'success', data }
        })
    ).pipe(delay(delayMs));
}

export function okCreated<T>(data: T, delayMs = MOCK_WRITE_DELAY_MS) {
    return of(
        new HttpResponse({
            status: 200,
            body: { code: 200, message: 'Created successfully', data }
        })
    ).pipe(delay(delayMs));
}

export function okUpdated<T>(data: T, delayMs = MOCK_WRITE_DELAY_MS) {
    return of(
        new HttpResponse({
            status: 200,
            body: { code: 200, message: 'Updated successfully', data }
        })
    ).pipe(delay(delayMs));
}

export function okDeleted(delayMs = MOCK_WRITE_DELAY_MS) {
    return of(
        new HttpResponse({
            status: 200,
            body: { code: 200, message: 'Deleted successfully' }
        })
    ).pipe(delay(delayMs));
}
